import React, { useState, useEffect, useCallback, Fragment, useRef } from "react";
import "./findings.scss";
import { useParams } from "react-router-dom";
import ltimLogo from "../../../../../images/LTM.png";
import Observations from "./observations/Observations";
import Comments from "./comments/Comments";
import { db } from "../../../../../database";
import Loading from "../../../../layout/loader/Loading";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../httpInterceptor";

// Constants for localStorage
const FINDINGS_FORM_DRAFT_KEY_PREFIX = "findings_draft";

interface Comment {
  created_by?: string;
  created_date?: string;
  comment?: string;
}

interface Finding {
  srlNo: string;
  observations: string;
  type: string;
  assignedBy: string;
  assignedTo: string;
  status: string;
  comments?: Comment[];
  created_date: string;
  modified_date?: string;
  // Evidence URLs returned from backend (normalized in API)
  evidence?: string[];
}
interface ObservationData {
  projectid: string | undefined;
  set_name: string | undefined;
  observations: Partial<Finding>[];
}

interface FindingsProps {
  onFindingsUpdate?: (data: any[]) => void;
}

const Findings: React.FC<FindingsProps> = ({ onFindingsUpdate }) => {
  const { statusOptions, typeOptions } = db;
  const params = useParams();
  const [id, setId] = useState<number>(0);
  const [findingsData, setFindingsData] = useState<any[]>([]);
  const [newFinding, setNewFinding] = useState<Partial<Finding> | null>(null);
  const [loading, setLoading] = useState(true);


  const [newFindingDate, setNewFindingDate] = useState(
    `${new Date().toISOString().split("T")[0]}`
  );

  const [newComment, setNewComment] = useState<string>("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedObservations, setExpandedObservations] = useState<
    number | null
  >(null);
  const [expandedComments, setExpandedComments] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [srlNo, setSrlNo] = useState<number>(1);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [hasRestoredDraft, setHasRestoredDraft] = useState<boolean>(false);
  const [hasRestoredEdit, setHasRestoredEdit] = useState<boolean>(false);
  
  // Add new states for file handling
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showFileModal, setShowFileModal] = useState<boolean>(false);
  const [currentFindingFiles, setCurrentFindingFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Ref to track when we just saved successfully to prevent unsaved changes warning
  const justSavedRef = useRef<boolean>(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [assignedByFilter, setAssignedByFilter] = useState<string>("");
  const [assignedToFilter, setAssignedToFilter] = useState<string>("");

  // Helper function to save draft comment to localStorage
  const saveDraftComment = (comment: string, isEditing = false, findingId?: string) => {
    if (isEditing && findingId) {
      // When editing an existing finding's comment
      const editKey = `${getLocalStorageKey()}_edit_${findingId}_comment`;
      localStorage.setItem(editKey, comment);
    } else {
      // When adding a new finding's comment
      localStorage.setItem(`${getLocalStorageKey()}_comment`, comment);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const commentValue = e.target.value;
    setNewComment(commentValue);

    // Autosave comment based on context (new finding vs editing)
    if (isEditing && editingIndex !== null && newFinding) {
      saveDraftComment(commentValue, true, newFinding.srlNo);
    } else {
      saveDraftComment(commentValue);
    }
  };
  const handleAddClick = () => {
    // Check if there's a saved draft finding
    const savedDraft = localStorage.getItem(getLocalStorageKey());
    const savedComment = localStorage.getItem(`${getLocalStorageKey()}_comment`);

    // Update the current date whenever Add Findings is clicked
    const currentDate = new Date();
    const formattedDate = `${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}-${currentDate.getFullYear()}`;
    setNewFindingDate(currentDate.toISOString().split("T")[0]);

    let initialFinding = {
      srlNo: String(
        !findingsData || findingsData.length < 1
          ? srlNo
          : (findingsData[0]?.observations?.length || 0) + 1
      ),
      observations: "",
      type: "",
      assignedBy: `${sessionStorage.getItem(
        "PBT_FIRSTNAME"
      )} ${sessionStorage.getItem("PBT_LASTNAME")}`,
      assignedTo: "",
      status: "",
      comments: [],
      created_date: formattedDate, // Set to current date in MM-DD-YYYY format
    };

    // If there's a saved draft, use it
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        initialFinding = {
          ...initialFinding,
          ...parsedDraft
        };

        // Restore saved comment if available
        if (savedComment) {
          setNewComment(savedComment);
        }

        toast.info("Draft finding restored. You can continue where you left off.");
        setHasRestoredDraft(true);
      } catch (error) {
        console.error("Error parsing saved draft:", error);
        // Continue with empty form if there's an error
      }
    }

    setNewFinding(initialFinding);
    setIsEditing(false);
    setOpenAdd(true);
  };

  const handleEditClick = (index: number, id: number) => {
    setId(id);

    // Check if there's a saved draft for this specific finding
    const editKey = `${getLocalStorageKey()}_edit_${id}`;
    const savedEditDraft = localStorage.getItem(editKey);
    const savedComment = localStorage.getItem(`${editKey}_comment`);

    if (savedEditDraft) {
      try {
        const parsedDraft = JSON.parse(savedEditDraft);
        setNewFinding(parsedDraft);

        if (savedComment) {
          setNewComment(savedComment);
        }

        toast.info("Draft changes restored. You can continue where you left off.");
        setHasRestoredEdit(true);
      } catch (error) {
        console.error("Error parsing saved edit draft:", error);
        setNewFinding(findingsData[0].observations[index]);
      }
    } else {
      const obs = findingsData[0].observations[index];
      setNewFinding(obs);
      // Pre-fill textarea with the latest comment
      if (Array.isArray(obs.comments) && obs.comments.length > 0) {
        setNewComment(obs.comments[obs.comments.length - 1].comment || "");
      }
    }

    setEditingIndex(index);
    setIsEditing(true);
    if (findingsData[0].observations[index] === index) {
      setNewFinding(null);
    }
    setOpenAdd(false);
  };
  // Helper function to generate a findings storage key
  const getLocalStorageKey = useCallback(() => {
    return `${FINDINGS_FORM_DRAFT_KEY_PREFIX}_${params.proj_id}_${params.set_name}`;
  }, [params.proj_id, params.set_name]);

  // Helper function to save draft finding to localStorage
  const saveDraftFinding = (finding: Partial<Finding> | null) => {
    if (finding) {
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(finding));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    field: keyof Finding
  ) => {
    if (newFinding) {
      const updatedFinding = {
        ...newFinding,
        [field]: e.target.value,
      };

      setNewFinding(updatedFinding);

      // Determine which key to use based on whether we're editing or adding
      if (isEditing && editingIndex !== null) {
        // When editing an existing finding
        const editKey = `${getLocalStorageKey()}_edit_${newFinding.srlNo}`;
        localStorage.setItem(editKey, JSON.stringify(updatedFinding));
      } else {
        // When adding a new finding
        saveDraftFinding(updatedFinding);
      }

      if (field === "assignedTo") {
        fetchSuggestions(e.target.value);
      }
    }
  };
  const handleCancelClick = () => {
    // If editing, check if there are saved changes
    if (isEditing && newFinding) {
      const editKey = `${getLocalStorageKey()}_edit_${newFinding.srlNo}`;
      const hasSavedEdits = localStorage.getItem(editKey);
      const hasSavedComments = localStorage.getItem(`${editKey}_comment`);

      if (hasSavedEdits || hasSavedComments) {
        // Ask user if they want to discard changes
        if (window.confirm("You have unsaved changes. Are you sure you want to cancel?")) {
          // Clear the saved data
          localStorage.removeItem(editKey);
          localStorage.removeItem(`${editKey}_comment`);
        } else {
          // User chose to continue editing
          return;
        }
      }
    } else if (!isEditing && openAdd) {
      // If adding a new finding, check if there's any draft data
      const hasDraft = localStorage.getItem(getLocalStorageKey());
      const hasDraftComment = localStorage.getItem(`${getLocalStorageKey()}_comment`);

      if ((hasDraft && JSON.parse(hasDraft).observations) || hasDraftComment) {
        // Ask user if they want to discard the draft
        if (window.confirm("You have an unsaved draft. Are you sure you want to cancel?")) {
          // Clear the saved draft
          localStorage.removeItem(getLocalStorageKey());
          localStorage.removeItem(`${getLocalStorageKey()}_comment`);
        } else {
          // User chose to continue editing
          return;
        }
      }
    }

    // Clear form data
    setNewFinding(null);
    setEditingIndex(null);
    setHasRestoredDraft(false);
    setHasRestoredEdit(false);
    setIsEditing(false);
    setOpenAdd(false);
    setNewComment("");
  };
  const convertISOToMMDDYYYY = useCallback((isoDate: string): string => {
    if (isoDate) {
      const [datePart] = isoDate.split("T"); // "2025-06-20"
      const [year, month, day] = datePart.split("-");
      return `${month}-${day}-${year}`;
    }
    return "";
  }, []);


  // const fetchFindingsData = async () => {
  //   try {
  //     const res = await axiosInstance.get(
  //       `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`
  //     );
  //     console.log("res", res);
  //     setFindingsData(res.data);

  //     if (onFindingsUpdate) {
  //       onFindingsUpdate(res.data); // Send data to parent
  //     }
  //   } catch (error) {
  //     console.error("Error fetching the data", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchFindingsData = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`
      );
      console.log("res", res);

      // Convert created_date for each observation
      const updatedData = res.data.map((item: any) => ({
        ...item,
        observations: item.observations.map((obs: any) => ({
          ...obs,
          created_date: convertISOToMMDDYYYY(obs.created_date),
        })),
      }));

      setFindingsData(updatedData);

      if (onFindingsUpdate) {
        onFindingsUpdate(updatedData); // Send updated data to parent
      }
    } catch (error) {
      console.error("Error fetching the data", error);
    } finally {
      setLoading(false);
    }
  }, [params.proj_id, params.set_name, onFindingsUpdate, convertISOToMMDDYYYY]);

  const fetchSuggestions = async (search: string) => {
    if (!search) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/getPSNumber/?search=${search}`
      );
      const result = response.data
        .filter((user: any) => {
          if (!search) {
            return false;
          }
          const searchText = search.toLowerCase();
          const fullName = user.name.toLowerCase();
          return fullName.startsWith(searchText);
        })
        .map((user: any) => user.name);
      setSuggestions(result);
      setNames(result);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  const handleSaveClick = async () => {
    const newCommentObject: Comment = {
      created_by: `${sessionStorage.getItem(
        "PBT_FIRSTNAME"
      )} ${sessionStorage.getItem("PBT_LASTNAME")}`,
      created_date: `${new Date().toISOString().split("T")[0]}`,
      comment: newComment,
    };
    console.log(newCommentObject);
    if (newFinding) {
      const updatedFinding: Finding = {
        ...newFinding,
        comments: newComment ? [...(newFinding.comments || []), newCommentObject] : newFinding.comments || [],
      } as Finding;
      if (editingIndex !== null) {
        // Comment is now optional when status changes
        // const originalStatus = findingsData[0].observations[editingIndex].status;
        // if (originalStatus !== newFinding.status && newComment === "") {
        //   toast.error("Please Add a Comment when changing Status");
        //   return;
        // }

        try {
          const UpdatedObservationData = {
            projectid: params.proj_id,
            set: params.set_name,
            srlNo: id,
            observations: newFinding.observations,
            type: newFinding.type,
            assignedBy: newFinding.assignedBy,
            assignedTo: newFinding.assignedTo,
            status: newFinding.status,
            comments: newComment ? [newCommentObject] : [],
            created_date: (() => {
              if (newFinding.created_date) {
                // Convert MM-DD-YYYY to YYYY-MM-DD for backend
                const [mm, dd, yyyy] = newFinding.created_date.split("-");
                return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
              }
              return "";
            })(),
          };
          console.log("=== SAVING EDITED FINDING ===");
          console.log("Original finding at index:", editingIndex, findingsData[0].observations[editingIndex]);
          console.log("New finding state:", newFinding);
          console.log("UpdatedObservationData being sent to API:", UpdatedObservationData);
          console.log("API URL:", `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`);

          const url = `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`;

          let res;
          if (selectedFiles.length > 0) {
            const formData = new FormData();
            formData.append("body", JSON.stringify(UpdatedObservationData));
            selectedFiles.forEach((file) => {
              formData.append("attachments", file);
            });

            res = await axiosInstance.patch(url, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          } else {
            res = await axiosInstance.patch(url, UpdatedObservationData);
          }

          console.log("=== API RESPONSE ===", res);
          console.log("Response status:", res.status);
          console.log("Response data:", res.data);

          // Clear saved data after successful submission BEFORE fetching new data
          if (newFinding && newFinding.srlNo) {
            const editKey = `${getLocalStorageKey()}_edit_${newFinding.srlNo}`;
            localStorage.removeItem(editKey);
            localStorage.removeItem(`${editKey}_comment`);
          }

          // Clear states immediately to prevent unsaved changes warning
          setNewComment("");
          setNewFinding(null);
          setEditingIndex(null);
          setIsEditing(false);
          setHasRestoredEdit(false);
          setOpenAdd(false);
          setSelectedFiles([]);
          
          // Mark that we just saved to skip the warning temporarily
          justSavedRef.current = true;
          setTimeout(() => {
            justSavedRef.current = false;
          }, 1000);

          // Fetch fresh data from backend to ensure UI is in sync
          console.log("=== FETCHING FRESH DATA ===");
          await fetchFindingsData();
          console.log("=== FRESH DATA FETCHED ===");
          console.log("Updated findingsData:", findingsData);
          
          toast.success("Data saved successfully");
          console.log("updatedInformation added in db", res);
        } catch (error) {
          console.log(error);
          toast.error("Failed to save changes. Your data has been preserved and you can try again.");
          return; // Exit early to prevent clearing the form
        }
      } else {
        setFindingsData((prevFindings) => [
          ...prevFindings,
          newFinding as Finding,
          updatedFinding,
        ]);
        try {
          const UpdatedObservationData: ObservationData = {
            projectid: params.proj_id,
            set_name: params.set_name,
            observations: [updatedFinding],
          };
          if (
            !newFinding.observations ||
            !newFinding.type ||
            !newFinding.assignedTo ||
            !newFinding.status
          ) {
            toast.error("Please Fill All The Fields to Save");
          } else if (!names.includes(newFinding.assignedTo)) {
            toast.error("Please Select a Name from Suggestion");
          } else {
            try {
              const url = `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`;

              let res;
              if (selectedFiles.length > 0) {
                const formData = new FormData();
                formData.append("body", JSON.stringify(UpdatedObservationData));
                selectedFiles.forEach((file) => {
                  formData.append("attachments", file);
                });

                res = await axiosInstance.post(url, formData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                  },
                });
              } else {
                res = await axiosInstance.post(url, UpdatedObservationData);
              }

              // Clear saved draft data after successful submission
              localStorage.removeItem(getLocalStorageKey());
              localStorage.removeItem(`${getLocalStorageKey()}_comment`);

              setHasRestoredDraft(false);
              fetchFindingsData();
              toast.success("Findings and observation data have been saved successfully");
              setNewComment("");
              setNewFinding(null);
              setSelectedFiles([]);
              setOpenAdd(false);
            } catch (apiError) {
              console.error("API Error:", apiError);
              toast.error("Failed to save findings. Your data has been preserved and you can try again.");
              // Data is already saved to localStorage, so we keep it for retry
              return; // Exit early to prevent clearing the form
            }
          }
        } catch (error) {
          console.log(error);
          toast.error("An error occurred. Your data has been preserved.");
        }
        setNewFinding(null);
        setOpenAdd(false);
      }
    }
  };
  useEffect(() => {
    fetchFindingsData();
  }, [params.proj_id, fetchFindingsData]);

  // Add event listeners to warn users before leaving the page with unsaved data
  useEffect(() => {
    // Check if there are any unsaved drafts
    const checkForUnsavedDrafts = () => {
      // Check for new finding draft
      const hasDraft = localStorage.getItem(getLocalStorageKey());
      const hasDraftComment = localStorage.getItem(`${getLocalStorageKey()}_comment`);

      // Check for edit drafts (we'll only check if we're currently editing)
      let hasEditDraft = false;
      if (isEditing && newFinding && newFinding.srlNo) {
        const editKey = `${getLocalStorageKey()}_edit_${newFinding.srlNo}`;
        hasEditDraft = Boolean(
          localStorage.getItem(editKey) ||
          localStorage.getItem(`${editKey}_comment`)
        );
      }

      return (hasDraft && JSON.parse(hasDraft).observations) ||
        hasDraftComment ||
        hasEditDraft;
    };

    // Event handler for before unload
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip warning if we just saved
      if (justSavedRef.current) {
        return;
      }
      
      if (checkForUnsavedDrafts()) {
        // Standard way to show a confirmation dialog before leaving
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditing, newFinding, getLocalStorageKey]);

  const handleSuggestionClick = (suggestion: string) => {
    setNewFinding((prevState) => ({
      ...prevState,

      assignedTo: suggestion,
    }));
    setSuggestions([]);
  };

  const handleSuggestionBlur = () => {
    setSuggestions([]);
  };
  const showError = () => {
    if (newFinding?.assignedTo && suggestions.includes(newFinding.assignedTo)) {
      return toast.error("message");
    }
    return null;
  };

  const [selectedContent, setSelectedContent] = useState<string | null>(null);
  const [isObservationsOpen, setIsObservationsOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [selectedCommentContent, setCommentSelectedContent] = useState<
    string | null
  >(null);

  const handleOpenObservations = (content: string, index: number) => {
    setSelectedContent(content);
    setExpandedObservations(index);
    setIsObservationsOpen(true);
  };
  const handleCloseObservations = () => {
    setIsObservationsOpen(false);
  };
  const handleOpenComments = (content: any, index: number) => {
    setIsCommentsOpen(true);
    setExpandedComments(index);
    setCommentSelectedContent(content);
  };
  const handleCloseComments = () => {
    setIsCommentsOpen(false);
  };

  const handleOpenEvidence = (files: string[]) => {
    setCurrentFindingFiles([...files].reverse());
    setShowFileModal(true);
  };

  const handleFileSelectAndUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (isEditing && editingIndex !== null && newFinding) {
      try {
        setIsUploading(true);
        const uploadData = {
          projectid: params.proj_id,
          set: params.set_name,
          srlNo: id,
          observations: newFinding.observations,
          type: newFinding.type,
          assignedBy: newFinding.assignedBy,
          assignedTo: newFinding.assignedTo,
          status: newFinding.status,
          comments: [],
          created_date: (() => {
            if (newFinding.created_date) {
              const [mm, dd, yyyy] = newFinding.created_date.split("-");
              return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
            }
            return "";
          })(),
        };
        const formData = new FormData();
        formData.append("body", JSON.stringify(uploadData));
        files.forEach((file) => formData.append("attachments", file));
        const url = `${process.env.REACT_APP_OBSERVATION_SERVICE_BASE_URL}/observations/${params.proj_id}/${params.set_name}`;
        await axiosInstance.patch(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${files.length} file(s) uploaded successfully`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        await fetchFindingsData();
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload file. Please try again.");
      } finally {
        setIsUploading(false);
      }
    } else {
      setSelectedFiles(files);
    }
  };

  if (loading) {
    return <Loading />;
  }

  // Filter findings based on search and dropdown filters
  const filteredFindings = findingsData && findingsData.length > 0 && Array.isArray(findingsData[0]?.observations)
    ? findingsData[0].observations.filter((finding: any) => {
        const matchesSearch = !searchQuery || 
          finding.observations?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          finding.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          finding.assignedBy?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          finding.assignedTo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          finding.status?.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = !statusFilter || finding.status === statusFilter;
        const matchesType = !typeFilter || finding.type === typeFilter;
        const matchesAssignedBy = !assignedByFilter || finding.assignedBy === assignedByFilter;
        const matchesAssignedTo = !assignedToFilter || finding.assignedTo === assignedToFilter;
        
        return matchesSearch && matchesStatus && matchesType && matchesAssignedBy && matchesAssignedTo;
      })
    : [];

  // Helper function to get file icon based on file type or extension
  const getFileIcon = (type: string): string => {
    const lower = (type || '').toLowerCase();
    if (lower.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(lower)) return '🖼️';
    if (lower.includes('pdf') || lower === 'pdf') return '📄';
    if (lower.includes('word') || lower.includes('doc') || ['doc', 'docx'].includes(lower)) return '📝';
    if (lower.includes('sheet') || lower.includes('excel') || ['xls', 'xlsx', 'csv'].includes(lower)) return '📊';
    if (lower.includes('video') || ['mp4', 'mov', 'avi', 'mkv'].includes(lower)) return '🎥';
    if (lower.includes('audio') || ['mp3', 'wav', 'aac'].includes(lower)) return '🎵';
    if (lower.includes('zip') || lower.includes('compressed') || ['zip', 'rar', '7z'].includes(lower)) return '📦';
    return '📎';
  };

  const getFileNameFromUrl = (url: string): string => {
    try {
      const cleanUrl = url.split('?')[0];
      const parts = cleanUrl.split('/');
      const name = parts[parts.length - 1] || 'Evidence';
      return decodeURIComponent(name);
    } catch {
      return 'Evidence';
    }
  };

  return (
    <div className="findings">
      {/* Search and Filter Section */}
      <div className="findings-header">
        <div className="search-bar-container">
          <i className="fa-solid fa-search search-icon"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Search findings (observation, person, status...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filters-container">
          <select
            className="filter-dropdown"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Type ▼</option>
            {typeOptions.map((type: any) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          
          <select
            className="filter-dropdown"
            value={assignedByFilter}
            onChange={(e) => setAssignedByFilter(e.target.value)}
          >
            <option value="">Assigned By ▼</option>
            {Array.from(new Set(findingsData[0]?.observations?.map((f: any) => f.assignedBy).filter(Boolean) || [])).map((name: any) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          
          <select
            className="filter-dropdown"
            value={assignedToFilter}
            onChange={(e) => setAssignedToFilter(e.target.value)}
          >
            <option value="">Assigned To ▼</option>
            {Array.from(new Set(findingsData[0]?.observations?.map((f: any) => f.assignedTo).filter(Boolean) || [])).map((name: any) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>


          <select
            className="filter-dropdown"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status ▼</option>
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="action-buttons-container">
          {openAdd === false && !isEditing && (
            <button
              className="add-finding-btn"
              onClick={handleAddClick}
            >
              Add Finding
            </button>
          )}
          <button
            className="save-finding-btn"
            onClick={handleSaveClick}
          >
            Save
          </button>
        </div>
      </div>
      <table className="findingsTable">
        <thead>
          <tr>
            <th>S.No</th>
            <th>Observation</th>
            <th>Type</th>
            <th>Assigned By</th>
            <th>Assigned To</th>
            <th>Status</th>
            <th>Remarks</th>
            <th>Evidence</th>
            <th>Last Modified Date</th>
            <th>Edit</th>
          </tr>
        </thead>
        <tbody>
          {filteredFindings.map((finding: any, index: number) => (
              <React.Fragment key={index}>
                {editingIndex === index && hasRestoredEdit && (
                  <tr>
                    <td colSpan={9} style={{ padding: "8px", backgroundColor: "#e6f7ff", color: "#0070f3", fontSize: "14px", textAlign: "center", border: "1px solid #91d5ff" }}>
                      <i className="fa-solid fa-info-circle" style={{ marginRight: "8px", color: "#00008B" }}></i>
                      Draft changes have been restored. You can continue editing where you left off.
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ width: "5%", textAlign: 'center', fontWeight: '500' }}>{finding.srlNo}</td>
                  <td className="observations" style={{ width: "30%" }}>
                    <span style={{ color: '#1f2937' }}>
                      {finding.observations && finding.observations.length > 100
                        ? `${finding.observations.slice(0, 100)}...`
                        : finding.observations}
                    </span>
                    {finding.observations && finding.observations.length > 100 && (
                      <span 
                        className="observations-popup"
                        onClick={() => handleOpenObservations(finding.observations, index)}
                        style={{ color: '#3b82f6', cursor: 'pointer', marginLeft: '4px', fontWeight: '500' }}
                      >
                        more
                      </span>
                    )}
                    <Observations
                      isOpen={
                        isObservationsOpen && expandedObservations === index
                      }
                      onClose={handleCloseObservations}
                      content={finding.observations}
                    />
                  </td>
                  <td style={{ width: "10%" }}>
                    {editingIndex === index ? (
                      <select
                        className={`typeInput ${hasRestoredEdit ? "restored-field" : ""}`}
                        value={newFinding?.type || finding.type}
                        required
                        onChange={(e) => handleChange(e, "type")}
                        style={hasRestoredEdit ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                      >
                        <option value="">Type</option>
                        {typeOptions.map((type: any) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      finding.type
                    )}
                  </td>
                  <td style={{ width: "12%" }}>{finding.assignedBy}</td>
                  <td style={{ width: "12%" }}>
                    {editingIndex === index ? (
                      <div className="input-container">
                        <input
                          className={`assignedToInput ${hasRestoredEdit ? "restored-field" : ""}`}
                          type="text"
                          value={newFinding?.assignedTo || finding.assignedTo}
                          onChange={(e) => handleChange(e, "assignedTo")}
                          onFocus={(e) => fetchSuggestions(e.target.value)}
                          onBlur={handleSuggestionBlur}
                          required
                          autoComplete="off"
                          style={hasRestoredEdit ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                        />
                        {suggestions && suggestions.length > 0 && (
                          <ul className="suggestions">
                            {suggestions.map((suggestion: string, idx: number) => (
                              <li
                                key={idx}
                                onMouseDown={() => handleSuggestionClick(suggestion)}
                              >
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      finding.assignedTo
                    )}
                  </td>
                  <td style={{ width: "10%" }}>
                    {editingIndex === index ? (
                      <select
                        className={`statusInput ${hasRestoredEdit ? "restored-field" : ""}`}
                        value={newFinding?.status || finding.status}
                        onChange={(e) => handleChange(e, "status")}
                        style={hasRestoredEdit ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                      >
                        <option defaultValue="Status">Status</option>
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`status-badge status-${finding.status?.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: 
                            finding.status === 'In Progress' ? '#3b82f6' :
                            finding.status === 'Completed' ? '#22c55e' :
                            finding.status === 'Closed' ? '#f7aa05' :
                            finding.status === 'Open' ? '#ef4444' :
                            'transparent',
                          color: 'white'
                        }}
                      >
                        {finding.status}
                      </span>
                    )}
                  </td>

                  <td style={{ width: "10%", maxHeight: '50px', overflow: 'hidden' }}>
                    {editingIndex === index ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <textarea
                          className={`commentsInput ${hasRestoredEdit ? "restored-field" : ""}`}
                          value={newComment}
                          placeholder="Add new remark..."
                          onChange={handleCommentChange}
                          style={hasRestoredEdit ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                        />
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const comments = Array.isArray(finding?.comments) ? finding.comments : [];
                          const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
                          const latestText = latestComment?.comment || '';
                          const hasMore = comments.length > 1 || latestText.length > 40;
                          return (
                            <>
                              <span style={{ color: '#1f2937', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {latestText.length > 40 ? `${latestText.slice(0, 40)}...` : latestText || '-'}
                              </span>
                              {hasMore && (
                                <span
                                  className="observations-popup"
                                  onClick={() => handleOpenComments(comments, index)}
                                  style={{ color: '#3b82f6', cursor: 'pointer', marginLeft: '4px', fontWeight: '500', whiteSpace: 'nowrap' }}
                                >
                                  more
                                </span>
                              )}
                              <Comments
                                isOpen={isCommentsOpen && expandedComments === index}
                                onClose={handleCloseComments}
                                content={comments}
                              />
                            </>
                          );
                        })()}
                      </>
                    )}
                  </td>
                  <td style={{ width: "10%", textAlign: 'center' }}>
                    {editingIndex === index ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                        {Array.isArray(finding.evidence) && finding.evidence.length > 0 && (
                          <div style={{ marginBottom: '4px', width: '100%' }}>
                            {(() => {
                              const evidence = finding.evidence as string[];
                              const latest = evidence[evidence.length - 1];
                              const hasMore = evidence.length > 1;
                              return (
                                <>
                                  <div style={{ marginBottom: '2px' }}>
                                    <a href={latest} target="_blank" rel="noopener noreferrer"
                                      style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'underline', wordBreak: 'break-all' }}>
                                      {getFileNameFromUrl(latest)}
                                    </a>
                                  </div>
                                  {hasMore && (
                                    <span
                                      onClick={() => handleOpenEvidence(evidence)}
                                      style={{ fontSize: '11px', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}
                                    >
                                      +{evidence.length - 1} more
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileSelectAndUpload}
                          style={{ display: 'none' }}
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <i className="fa-solid fa-upload" style={{ marginRight: '4px' }}></i>
                          Upload
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'left' }}>
                        {Array.isArray(finding.evidence) && finding.evidence.length > 0 ? (
                          (() => {
                            const evidence = finding.evidence as string[];
                            const latest = evidence[evidence.length - 1];
                            const hasMore = evidence.length > 1;
                            return (
                              <>
                                <div style={{ marginBottom: '2px' }}>
                                  <a href={latest} target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'underline', wordBreak: 'break-all' }}>
                                    {getFileNameFromUrl(latest)}
                                  </a>
                                </div>
                                {hasMore && (
                                  <span
                                    onClick={() => handleOpenEvidence(evidence)}
                                    style={{ fontSize: '12px', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}
                                  >
                                    +{evidence.length - 1} more
                                  </span>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>No files</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ width: "10%", textAlign: 'center', fontSize: '13px', color: '#374151' }}>
                    {finding.modified_date 
                      ? new Date(finding.modified_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : finding.created_date
                      ? new Date(finding.created_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : '-'
                    }
                  </td>
                  <td className="actions-cell">
                    {editingIndex === index ? (
                      <i
                        className="fa-solid fa-xmark action-icon"
                        style={{ cursor: "pointer", color: "#ef4444" }}
                        onClick={handleCancelClick}
                        title="Cancel"
                      ></i>
                    ) : (
                      <i
                        className="fa-solid fa-pen-to-square action-icon"
                        style={{ cursor: "pointer", color: "#3b82f6" }}
                        onClick={() => handleEditClick(index, finding.srlNo)}
                        title="Edit"
                      ></i>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          {newFinding !== null && editingIndex === null && hasRestoredDraft && (
            <tr>
              <td colSpan={9} style={{ padding: "8px", backgroundColor: "#f8e8daff", color: "#FF5E4F", fontSize: "14px", textAlign: "center", border: "1px solid #91d5ff" }}>
                <i className="fa-solid fa-info-circle" style={{ marginRight: "8px", color: "#f23c2cff" }}></i>
                Draft data has been restored. You can continue where you left off.
              </td>
            </tr>
          )}
          {newFinding !== null && editingIndex === null && (
            <tr>
              <td style={{ width: "2%" }}>{newFinding.srlNo}</td>
              <td style={{ width: "20%" }}>
                <textarea
                  className={`observationsInput ${hasRestoredDraft && newFinding.observations ? "restored-field" : ""}`}
                  value={newFinding.observations}
                  required
                  onChange={(e) => handleChange(e, "observations")}
                  style={hasRestoredDraft && newFinding.observations ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                />
              </td>
              <td style={{ width: "11%" }}>
                <select
                  className={`typeInput ${hasRestoredDraft && newFinding.type ? "restored-field" : ""}`}
                  value={newFinding.type}
                  required
                  onChange={(e) => handleChange(e, "type")}
                  style={hasRestoredDraft && newFinding.type ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                >
                  <option defaultValue="Type">Type</option>
                  {typeOptions.map((type: any) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ width: "12%" }}>
                <p className="assignedByInput">{newFinding.assignedBy}</p>
              </td>
              <td className="input-container" style={{ width: "12%" }}>
                <input
                  className={`assignedToInput ${hasRestoredDraft && newFinding.assignedTo ? "restored-field" : ""}`}
                  type="text"
                  value={newFinding?.assignedTo ?? ""}
                  onChange={(e) => handleChange(e, "assignedTo")}
                  onFocus={(e) => fetchSuggestions(e.target.value)}
                  onBlur={handleSuggestionBlur}
                  required
                  autoComplete="off"
                  style={hasRestoredDraft && newFinding.assignedTo ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                />
                {suggestions && suggestions.length > 0 && (
                  <ul className="suggestions">
                    {suggestions.map((suggention: string, index: number) => (
                      <li
                        key={index}
                        onMouseDown={() => handleSuggestionClick(suggention)}
                      >
                        {suggention}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td style={{ width: "11%" }}>
                <select
                  className={`statusInput ${hasRestoredDraft && newFinding.status ? "restored-field" : ""}`}
                  value={newFinding?.status || ""}
                  required
                  onChange={(e) => handleChange(e, "status")}
                  style={hasRestoredDraft && newFinding.status ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                >
                  <option value="">Status</option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ width: "20%" }}>
                <textarea
                  className={`commentsInput ${hasRestoredDraft ? "restored-field" : ""}`}
                  value={newComment}
                  onChange={handleCommentChange}
                  style={hasRestoredDraft && newComment ? { borderColor: "#91d5ff", boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)" } : {}}
                />
              </td>
              <td style={{ width: "10%", textAlign: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelectAndUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fa-solid fa-upload" style={{ marginRight: '4px' }}></i>
                    Upload
                  </button>
                  {selectedFiles.length > 0 && (
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                      {selectedFiles.length} file(s) selected
                    </span>
                  )}
                </div>
              </td>
              <td style={{ width: "10%", textAlign: 'center', fontSize: '13px', color: '#374151' }}>
                {newFindingDate 
                  ? new Date(newFindingDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  : '-'
                }
              </td>

              <td className="actions-cell">
                <i
                  className="fa-solid fa-xmark action-icon"
                  style={{ cursor: "pointer", color: "#ef4444" }}
                  onClick={handleCancelClick}
                  title="Cancel"
                ></i>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Upload Loader Popup */}
      {isUploading && (
        <div className="upload-loader-overlay">
          <div className="upload-loader-popup">
            <img src={ltimLogo} alt="LTIMindtree" className="upload-loader-logo" />
            <div className="upload-loader-spinner"></div>
            <p className="upload-loader-text">File is being uploaded<span className="upload-loader-dots">...</span></p>
          </div>
        </div>
      )}

      {/* File Modal */}
      {showFileModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowFileModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Evidence Files</h3>
              <i
                className="fa-solid fa-xmark"
                style={{ cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}
                onClick={() => setShowFileModal(false)}
              ></i>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentFindingFiles.length > 0 ? (
                currentFindingFiles.map((url: string, index: number) => {
                  const name = getFileNameFromUrl(url);
                  const ext = (name.split('.').pop() || '').toLowerCase();
                  return (
                    <div 
                      key={index} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '24px' }}>{getFileIcon(ext)}</span>
                        <div>
                          <p style={{ margin: 0, fontWeight: '500', color: '#1f2937' }}>{name}</p>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ margin: 0, fontSize: '12px', color: '#3b82f6', textDecoration: 'underline' }}
                          >
                            Open
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>No files available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Findings;